import React from 'react';

import { 
  API, 
  Logger, 
  graphqlOperation,
}                     from 'aws-amplify';

import diff           from 'deep-diff';

import { 
  inlineJsonProps,
}                     from '../../utils/helpers';

import { 
  getResource, 
  listResources
}                     from '../../../graphql/queries';

const logger = new Logger('useResources');
  
const onMutateResource = `
  subscription OnMutateResource[args1] {
    onMutateResource[args2] {
      mutationType
      clientAccountId
      resourceId
      resourceType
      payload
    }
  }
`;

function resourcesReducer(state, action) {
  switch (action.type) {
    case "INIT":
      // TODO: reset condition is not working correctly leading to duplicates
      // so its disabled for now, something else in context is changing or 
      // otherwise causing the hook to re-render with same args
      // const reset=(state.resourceType !== action.resourceType) || 
      //             (state.clientAccountId !== action.clientAccountId);
      const reset=true;
      return { 
        ...state,
        resourceMap: reset?new Map():state.resourceMap,
        error: false,
        resourceType: action.resourceType,
        clientAccountId: action.clientAccountId,
      };
    case "LOAD":
      logger.debug("LOAD: ", action.chunk);
      const newResources=(action.chunk.items?action.chunk.items:[]).map( e => [e.ResourceId, inlineJsonProps(e)] );
      return {
        ...state,
        resourceMap: new Map([ ...state.resourceMap.entries(), ...newResources ]),
        nextToken: action.chunk.nextToken?action.chunk.nextToken:null,
      };
    case "LOADING":
      return { 
        ...state,
        loading: action.status,
      };
    case "ERROR":
      return { 
        ...state,
        error: action.message,
      };
    case "INSERT":
      let resource = JSON.parse(action.mutation.payload);
      resource['ClientAccountId']=action.mutation.clientAccountId;
      resource['ResourceId']=action.mutation.resourceId;
      resource['ResourceType']=action.mutation.resourceType;
      logger.debug(`INSERT ${action.mutation.resourceType} ${action.mutation.resourceId}`,resource);
      return { 
        ...state,
        resourceMap: state.resourceMap.set(resource.ResourceId,resource),
      };
    case "MODIFY":
      if(state.resourceMap.has(action.mutation.resourceId)) {
        const differences = JSON.parse(action.mutation.payload);
        const updatedResource = JSON.parse(JSON.stringify(state.resourceMap.get(action.mutation.resourceId)));
        const updatedAttrs=[];
        differences.forEach((change) => {
           diff.applyChange(updatedResource,null,change); 
           updatedAttrs.push(change.path.join('.'));
        });
        logger.debug(`MODIFY ${action.mutation.resourceType} ${updatedResource.ResourceId}: ${updatedAttrs.join(', ')} `,differences);
        return {
          ...state,
          resourceMap: state.resourceMap.set(updatedResource.ResourceId,updatedResource),
        };
      } else {
        return state;
      }
    case "MUTATE":
      return resourcesReducer(state, {
        type: action.mutation.mutationType,
        mutation: action.mutation,
      }); // INSERT, MODIFY or REMOVE
    case "REMOVE":
      logger.debug(`REMOVE ${action.mutation.resourceType} ${action.mutation.resourceId}`);
      if (state.resourceMap.delete(action.mutation.resourceId)) {
        return {
          ...state,
          resourceMap: new Map(state.resourceMap),
        };
      } else {
        return state; 
      }
    default:
      logger.error('UNKNOWN:',action.type);
      return state;
  }
}

function fmtQueryArgs(query, args1, args2) {
  const queryArgs1=query.replace('[args1]',args1);
  const queryArgs2=queryArgs1.replace('[args2]',args2);
  return queryArgs2;
}

export const useResources = (props) => {

  const [state, dispatch] = React.useReducer(resourcesReducer, { 
    resourceMap: new Map(),
    loading: false,
    error: null,
    nextToken: null,
    clientAccountId: null,
    resourceType: null,
  });
  
  const { 
    dataLakeRole, clientAccountId, resourceType, subscribed,
    loadMoreInputs, limit=2000 } = props;

  const fetchResources = async (variables) => {
    
    let hasMore=true;
    let maxRows=variables.limit;
    let retrievedRows=0;
    let nextToken=null;
    
    dispatch({ type: 'LOADING', status: true});
    dispatch({ type: 'ERROR', message: null});

    while(hasMore && retrievedRows<maxRows) {
      if(nextToken) {
        variables['nextToken']=nextToken;
      }

      try {
        const response = await API.graphql(graphqlOperation(listResources,variables));
        if(response.errors) {
          dispatch({ type: 'ERROR', message: response.errors[0].message});
        } else {
          const data = response.data.listResources;
          if(data.items) {
            dispatch({ type: 'LOAD', chunk: data });
            retrievedRows+=data.items.length;
          }
          if(data.nextToken) {
            nextToken=data.nextToken;
          } else {
            hasMore=false;
          }
        }
      } catch(error) {
        dispatch({ type: 'ERROR', message: error});
        hasMore=false;
      }
    }
    
    dispatch({ type: 'LOADING', status: false});
    
  };

  React.useEffect(() => {
    let subscription = null;
    if(dataLakeRole && 
        (clientAccountId || 
          ['Auditor', 'Admin'].indexOf(dataLakeRole) >= 0 )) {

      let variables = {
        limit
      };
      if (clientAccountId || resourceType) {
        variables['filter']={};
        if (clientAccountId) {
          variables.filter['ClientAccountId']={ eq: clientAccountId };
        }
        if (resourceType) {
          variables.filter['ResourceType']={ eq: resourceType };
        }
      }

      dispatch({ type: 'INIT', clientAccountId, resourceType });

      fetchResources(variables);
      
      const mutationType = (subscribed&&['INSERT', 'MODIFY', 'REMOVE'].indexOf(subscribed) >= 0)?subscribed:null;
  
      let subscriptionVariables = null;
      let subscriptionArgs1 = "";
      let subscriptionArgs2 = "";
      let args1 = [];
      let args2 = [];
      if (mutationType || clientAccountId || resourceType) {
        subscriptionVariables={};
        if (mutationType) {
          subscriptionVariables['MutationType'] = mutationType;
          args1.push('$MutationType: ID');
          args2.push('mutationType: $MutationType');
        }
        if (clientAccountId) {
          subscriptionVariables['ClientAccountId'] = clientAccountId;
          args1.push('$ClientAccountId: ID');
          args2.push('clientAccountId: $ClientAccountId');
        }
        if (resourceType) {
          subscriptionVariables['ResourceType'] = resourceType;
          args1.push('$ResourceType: ResourceType');
          args2.push('resourceType: $ResourceType');
        }
        subscriptionArgs1="(" + args1.join(',') + ")";
        subscriptionArgs2="(" + args2.join(',') + ")";
      }
  
      if(subscribed) {
        const subscribeOperation=fmtQueryArgs(onMutateResource,subscriptionArgs1,subscriptionArgs2);
        subscription=API.graphql(graphqlOperation(subscribeOperation,subscriptionVariables)).subscribe({
          next: data => {
            const {
              value: {
                data: { onMutateResource }
              }
            } = data;
            dispatch({
              type: 'MUTATE',
              mutation: onMutateResource,
            });
          }
        });
      }
    }
    
    return () => { 
      logger.debug("useResources cleanup!");
      if(subscription) {
        subscription.unsubscribe();
      }
    };

  }, [dataLakeRole, clientAccountId, resourceType, subscribed, loadMoreInputs, limit]);
  
  return { 
    resources: [...state.resourceMap.values()], 
    loading: state.loading, 
    error: state.error,
    moreResources: state.nextToken,
  };
};

function resourceReducer(state, action) {
  switch (action.type) {
    case "SET":
      logger.debug("SET: ", action.resource);
      return { 
        ...state,
        resource: inlineJsonProps(action.resource,2),
      };
    case "LOADING":
      return { 
        ...state,
        loading: action.status
      };
    case "ERROR":
      return { 
        ...state,
        error: action.message 
      };
    case "MODIFY":
      const differences=JSON.parse(action.mutation.payload);
      const updatedResource=JSON.parse(JSON.stringify(state.resource));
      differences.forEach((change) => {
         diff.applyChange(updatedResource,null,change); 
      });
      logger.debug('MODIFY',state.resource, differences, updatedResource);
      return { 
        ...state,
        execution: updatedResource,
      };
    default:
      logger.error('UNKNOWN:',action.type);
      return state;
  }
}

export const useResource = (resourceId, clientAccountId=null, options={ query: getResource, subscribed: false }) => {

  const [state, dispatch] = React.useReducer(resourceReducer, { 
    execution: null, 
    loading: false,
    error: null
  });
  
  const { query, subscribed } = options;
  const inlineJsonDepth = options.inlineJsonDepth || 0;
  
  React.useEffect(() => {
    let unmounted = false;
    dispatch({ type: 'LOADING', status: true});
    dispatch({ type: 'ERROR', message: null});
    const variables = {
      ResourceId: resourceId
    };

    API.graphql(graphqlOperation(query,variables))
      .then(res => res.data.getResource)
      .then(item => {
        if(!unmounted) {
          dispatch({ type: 'LOADING', status: false});
          dispatch({ type: 'SET', resource: item});
        }
      })
      .catch(err => {
        if(!unmounted) {
          dispatch({ type: 'LOADING', status: false});
          dispatch({ type: 'ERROR', message: err});
        }
      });

    let subscription = null;

    let subscriptionVariables = {ResourceId: resourceId};
    let args1 = ["$ResourceId: ID"];
    let args2 = ["resourceId: $ResourceId"];
    if (clientAccountId) {
      subscriptionVariables['ClientAccountId'] = clientAccountId;
      args1.push('$ClientAccountId: ID');
      args2.push('clientAccountId: $ClientAccountId');
    }
    const subscriptionArgs1=args1.length?"(" + args1.join(',') + ")":"";
    const subscriptionArgs2=args2.length?"(" + args2.join(',') + ")":"";

    if(subscribed) {
      logger.debug("Subscribing resource");
      const subscribeOperation=fmtQueryArgs(onMutateResource,subscriptionArgs1,subscriptionArgs2);
      subscription=API.graphql(graphqlOperation(subscribeOperation,subscriptionVariables)).subscribe({
        next: data => {
          const {
            value: {
              data: { onMutateResource }
            }
          } = data;
          dispatch({ type: 'MODIFY', mutation: onMutateResource});
        }
      });
    }

    return () => { 
      unmounted = true;
      if(subscription) {
        logger.debug("Unsubscribing resource");
        subscription.unsubscribe();
      }
    };

  }, [resourceId, clientAccountId, query, inlineJsonDepth, subscribed]);
  
  return { 
    resource: state.resource,
    loading: state.loading,
    error: state.error,
  };
};

